import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { LogOut, LayoutDashboard, Calendar, ShieldCheck, MessageSquare, User, Users, Menu, X, Globe, Activity, Bell, Sun, Moon } from "lucide-react";
import { api } from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import LanguageSwitcher, { MobileLanguageSwitcher } from "@/components/LanguageSwitcher";

export default function Header() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const mobileMenuTriggerRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get('/notifications').then((r) => {
      setNotifications(r.data.notifications);
      setUnread(r.data.unread);
    }).catch((e) => {
      if (import.meta.env.DEV) console.warn("notifications fetch failed:", e);
    });
    const interval = setInterval(() => {
      api.get('/notifications').then((r) => {
        setNotifications(r.data.notifications);
        setUnread(r.data.unread);
      }).catch((e) => {
        if (import.meta.env.DEV) console.warn("notifications fetch failed:", e);
      });
    }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  useEffect(() => {
    if (!user || !('serviceWorker' in navigator) || !('PushManager' in window)) return;
    async function register() {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js');
        const sub = await reg.pushManager.getSubscription();
        if (sub) return;
        const r = await api.get('/push/vapid-key');
        const publicKey = r.data.publicKey;
        const newSub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: publicKey,
        });
        await api.post('/push/subscribe', newSub.toJSON());
      } catch (e) {
        if (import.meta.env.DEV) console.warn("push registration skipped:", e);
      }
    }
    register();
  }, [user]);

  useEffect(() => {
    function handleClick(e) {
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    await api.patch(`/notifications/${id}/read`);
    setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
  };

  const markAllRead = async () => {
    await api.patch('/notifications/read-all');
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const closeMobile = () => setMobileOpen(false);
  const closeMobileAndRestoreFocus = () => {
    setMobileOpen(false);
    mobileMenuTriggerRef.current?.focus();
  };
  const currentLanguage = i18n.language?.startsWith("ar") ? "العربية" : "English";
  const currentThemeLabel = theme === "dark" ? "Dark" : "Light";

  const linkClass = ({ isActive }) =>
    `px-3 py-2 text-xs tracking-[0.2em] uppercase font-semibold transition-all border-b-2 ${
      isActive
        ? "text-volt font-bold border-volt bg-volt/5"
        : "text-muted-foreground border-transparent hover:text-foreground hover:border-foreground/30"
    }`;

  return (
    <header className="glass sticky top-0 z-50">
      <div className="mx-auto flex items-center justify-between px-5 md:px-10 h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <svg className="h-7 w-7" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="16" cy="13" rx="9" ry="11" stroke="#CCFF00" strokeWidth="2.2" />
            <line x1="16" y1="24" x2="16" y2="30" stroke="#CCFF00" strokeWidth="2.5" strokeLinecap="round" />
            <polygon points="19,2 12,15 15.5,15 9,28 23,12 19,12 24,6" fill="#CCFF00" />
          </svg>
          <span className="font-heading text-2xl tracking-tight">Go<span className="text-volt">Captain</span></span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavLink to="/search" className={linkClass} data-testid="nav-explore">{t("nav.explore")}</NavLink>
          <NavLink to="/play" className={linkClass} data-testid="nav-play">{t("nav.play")}</NavLink>
          {user?.role === "owner" && (
            <NavLink to="/owner" className={linkClass} data-testid="nav-owner">{t("nav.owner")}</NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin" className={linkClass} data-testid="nav-admin">{t("nav.admin")}</NavLink>
          )}
          {user && (
            <>
              <NavLink to="/bookings" className={linkClass} data-testid="nav-bookings">{t("nav.bookings")}</NavLink>
            </>
          )}
        </nav>

        <div className="flex items-center gap-3">
          {!user && (
            <>
              <button
                onClick={toggleTheme}
                className="h-9 w-9 flex items-center justify-center border border-border hover:border-volt/50 transition-colors"
                data-testid="theme-toggle"
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
              >
                {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </button>
              <LanguageSwitcher />
            </>
          )}
          {user && (
            <button
              onClick={() => navigate("/chats")}
              className="hidden md:inline-flex h-9 w-9 items-center justify-center border border-border hover:border-volt/50 transition-colors"
              data-testid="header-chats-button"
              title={t("nav.chats")}
            >
              <MessageSquare className="h-4 w-4" />
            </button>
          )}
          {user && (
            <div className="relative" ref={notifRef}>
              <button
                onClick={() => setNotifOpen(!notifOpen)}
                className="relative h-9 w-9 flex items-center justify-center border border-border hover:border-volt/50 transition-colors"
                type="button"
                aria-label={t("nav.notifications")}
                aria-expanded={notifOpen}
                aria-controls="header-notifications"
              >
                <Bell className="h-4 w-4" />
                {unread > 0 && (
                  <span className="absolute -top-1 -end-1 h-4 w-4 bg-red-500 text-foreground text-[8px] flex items-center justify-center font-bold rounded-full">{unread > 9 ? '9+' : unread}</span>
                )}
              </button>
              {notifOpen && (
                <div
                  id="header-notifications"
                  className="fixed start-3 end-3 top-16 w-auto bg-card border border-border rounded-sm shadow-xl z-50 max-h-96 overflow-y-auto md:absolute md:start-auto md:end-0 md:top-auto md:mt-2 md:w-80"
                >
                  <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <span className="text-[10px] tracking-widest uppercase font-bold">{t("nav.notifications")}</span>
                    {unread > 0 && (
                      <button onClick={markAllRead} className="text-[9px] tracking-widest uppercase text-volt hover:underline">{t("nav.mark_all_read")}</button>
                    )}
                  </div>
                  {notifications.length === 0 && (
                    <div className="px-4 py-8 text-center text-muted-foreground/60 text-sm">{t("nav.no_notifications")}</div>
                  )}
                  {notifications.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => { markRead(n.id); if (n.link) navigate(n.link); setNotifOpen(false); }}
                      className={`w-full text-start px-4 py-3 border-b border-border/30 hover:bg-muted transition-colors ${n.read ? "" : "bg-volt/5"}`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${n.read ? "bg-transparent" : "bg-volt"}`} />
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-foreground/90">{n.title}</div>
                          <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{n.body}</div>
                          <div className="text-[9px] text-muted-foreground/60 mt-1">{n.created_at?.slice(0, 10)}</div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <span className="sr-only" aria-live="polite" data-testid="language-status">
            {t("common.language")}: {currentLanguage}
          </span>
          <button
            ref={mobileMenuTriggerRef}
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden inline-flex items-center justify-center h-9 w-9 border border-border hover:border-volt/50 transition-colors"
            type="button"
            aria-label={mobileOpen ? t("nav.close_menu") : t("nav.open_menu")}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
          {!user ? (
            <>
              <Link to="/login" data-testid="header-login-link">
                <button className="btn-outline rounded-sm px-4 py-2 text-xs">{t("nav.signin")}</button>
              </Link>
              <Link to="/register/host" data-testid="header-register-link" className="hidden sm:block">
                <button className="btn-volt rounded-sm px-4 py-2 text-xs">{t("nav.list_court")}</button>
              </Link>
            </>
          ) : (
            <DropdownMenu dir={i18n.dir()}>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 border border-border hover:border-volt/50 transition-colors rounded-sm px-3 py-2"
                >
                  <div className="w-7 h-7 bg-volt text-background flex items-center justify-center font-bold text-sm rounded-sm">
                    {(user.name || user.email).slice(0, 1).toUpperCase()}
                  </div>
                  <span className="hidden sm:inline text-xs uppercase tracking-widest font-semibold">
                    {user.name?.split(" ")[0] || "Account"}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="bg-card border border-border text-foreground rounded-sm min-w-[220px]"
              >
                <DropdownMenuLabel className="text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-border" />
                {user.role === "owner" && (
                  <DropdownMenuItem onClick={() => navigate("/owner")} data-testid="menu-owner-dash">
                    <LayoutDashboard className="h-4 w-4 me-2" /> {t("nav.owner")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                  <User className="h-4 w-4 me-2" /> {t("nav.profile")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/teams")} data-testid="menu-teams">
                  <Users className="h-4 w-4 me-2" /> {t("nav.teams")}
                </DropdownMenuItem>

                <DropdownMenuItem onClick={() => navigate("/bookings")} data-testid="menu-my-bookings">
                  <Calendar className="h-4 w-4 me-2" /> {t("nav.bookings")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/chats")} data-testid="menu-chats">
                  <MessageSquare className="h-4 w-4 me-2" /> {t("nav.chats")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/sessions")} data-testid="menu-sessions">
                  <Activity className="h-4 w-4 me-2" /> {t("nav.my_sessions")}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-border" />
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="menu-theme-trigger">
                    {theme === "dark" ? <Moon className="h-4 w-4 me-2" /> : <Sun className="h-4 w-4 me-2" />}
                    Theme
                    <DropdownMenuShortcut>{currentThemeLabel}</DropdownMenuShortcut>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="bg-card border border-border text-foreground rounded-sm min-w-[180px]">
                    <DropdownMenuLabel className="text-xs tracking-widest uppercase text-muted-foreground">
                      Select theme
                    </DropdownMenuLabel>
                    <DropdownMenuRadioGroup value={theme === "dark" ? "dark" : "light"}>
                      <DropdownMenuRadioItem
                        value="light"
                        onSelect={() => {
                          if (theme !== "light") toggleTheme();
                        }}
                        data-testid="menu-theme-light"
                      >
                        <Sun className="h-4 w-4 me-2" /> Light
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem
                        value="dark"
                        onSelect={() => {
                          if (theme !== "dark") toggleTheme();
                        }}
                        data-testid="menu-theme-dark"
                      >
                        <Moon className="h-4 w-4 me-2" /> Dark
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger data-testid="menu-language-trigger">
                    <Globe className="h-4 w-4 me-2" /> {t("common.language")}
                    <DropdownMenuShortcut>{currentLanguage}</DropdownMenuShortcut>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="bg-card border border-border text-foreground rounded-sm min-w-[180px]">
                    <DropdownMenuLabel
                      className={i18n.dir() === "rtl"
                        ? "text-sm text-muted-foreground"
                        : "text-xs tracking-widest uppercase text-muted-foreground"}
                    >
                      {t("common.select_language")}
                    </DropdownMenuLabel>
                    <DropdownMenuRadioGroup value={i18n.language?.startsWith("ar") ? "ar" : "en"}>
                      <DropdownMenuRadioItem
                        value="en"
                        onSelect={() => i18n.changeLanguage("en")}
                        data-testid="menu-language-en"
                      >
                        English
                      </DropdownMenuRadioItem>
                      <DropdownMenuRadioItem
                        value="ar"
                        onSelect={() => i18n.changeLanguage("ar")}
                        data-testid="menu-language-ar"
                      >
                        العربية
                      </DropdownMenuRadioItem>
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                {user.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="h-4 w-4 me-2" /> {t("nav.admin")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator className="bg-border" />
                <DropdownMenuItem
                  onClick={async () => { await logout(); navigate("/"); }}
                  data-testid="menu-logout"
                >
                  <LogOut className="h-4 w-4 me-2" /> {t("nav.signout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Mobile navigation */}
      {mobileOpen && (
        <div
          id="mobile-navigation"
          className="md:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain border-t border-border bg-background/95 backdrop-blur-lg"
        >
          <nav className="flex flex-col px-5 py-4 gap-1">
            <NavLink
              to="/search"
              onClick={closeMobile}
              className={({ isActive }) =>
                `px-3 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-colors rounded-sm ${
                  isActive ? "text-volt bg-volt/5" : "text-muted-foreground hover:text-foreground hover:bg-card"
                }`
              }
            >
              {t("nav.explore")}
            </NavLink>
            <NavLink
              to="/play"
              onClick={closeMobile}
              className={({ isActive }) =>
                `px-3 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-colors rounded-sm ${
                  isActive ? "text-volt bg-volt/5" : "text-muted-foreground hover:text-foreground hover:bg-card"
                }`
              }
            >
              {t("nav.play")}
            </NavLink>
            {user && (
              <>
                <NavLink
                  to="/teams"
                  onClick={closeMobile}
                  className={({ isActive }) =>
                    `px-3 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-colors rounded-sm ${
                      isActive ? "text-volt bg-volt/5" : "text-muted-foreground hover:text-foreground hover:bg-card"
                    }`
                  }
                >
                  {t("nav.teams")}
                </NavLink>
                <NavLink
                  to="/bookings"
                  onClick={closeMobile}
                  className={({ isActive }) =>
                    `px-3 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-colors rounded-sm ${
                      isActive ? "text-volt bg-volt/5" : "text-muted-foreground hover:text-foreground hover:bg-card"
                    }`
                  }
                >
                  {t("nav.bookings")}
                </NavLink>
                <NavLink
                  to="/chats"
                  onClick={closeMobile}
                  className={({ isActive }) =>
                    `px-3 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-colors rounded-sm ${
                      isActive ? "text-volt bg-volt/5" : "text-muted-foreground hover:text-foreground hover:bg-card"
                    }`
                  }
                >
                  {t("nav.chats")}
                </NavLink>
                <NavLink
                  to="/sessions"
                  onClick={closeMobile}
                  className={({ isActive }) =>
                    `px-3 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-colors rounded-sm ${
                      isActive ? "text-volt bg-volt/5" : "text-muted-foreground hover:text-foreground hover:bg-card"
                    }`
                  }
                >
                  {t("nav.my_sessions")}
                </NavLink>
              </>
            )}
            {user?.role === "owner" && (
              <NavLink
                to="/owner"
                onClick={closeMobile}
                className={({ isActive }) =>
                  `px-3 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-colors rounded-sm ${
                    isActive ? "text-volt bg-volt/5" : "text-muted-foreground hover:text-foreground hover:bg-card"
                  }`
                }
              >
                {t("nav.owner")}
              </NavLink>
            )}
            {user?.role === "admin" && (
              <NavLink
                to="/admin"
                onClick={closeMobile}
                className={({ isActive }) =>
                  `px-3 py-3 text-xs tracking-[0.2em] uppercase font-semibold transition-colors rounded-sm ${
                    isActive ? "text-volt bg-volt/5" : "text-muted-foreground hover:text-foreground hover:bg-card"
                  }`
                }
              >
                {t("nav.admin")}
              </NavLink>
            )}
            <MobileLanguageSwitcher onSelect={closeMobileAndRestoreFocus} />
          </nav>
        </div>
      )}
    </header>
  );
}
